import { StateContext } from "@ngxs/store";
import { parse } from "node-html-parser";

export type DigitBool = 1 | 0;

export type MoveDirection = "up" | "down";

export interface ObjectLiteral {
	[key: string]: any;
}

export interface CommonState<ErrorsType = string> {
	isFinishedLoading?: boolean;
	errors?: ErrorsType;
	updating?: boolean;
}

export interface CollectionState<T> extends CommonState {
	items?: T[];
}
export interface PaginatedCollectionState<T> extends CollectionState<T> {
	total?: number;
	from: number;
	to: number;
	lastID?: string;
	direction: string;
	limit: number;
	currentPage: number;
	isLoading?: boolean;
}

export interface ItemState<T> extends CommonState {
	item?: T;
	updatedSuccessfully?: boolean;
}

export const initialCollectionState = {
	isFinishedLoading: true,
	errors: null,
	items: [],
};
export const initialPaginatedCollectionState = {
	isFinishedLoading: true,
	errors: null,
	items: [],
	total: 0,
	from: undefined,
	to: undefined,
	lastID: Number.MAX_SAFE_INTEGER.toString(),
	direction: "older",
	limit: 20,
	currentPage: 1,
};

export const initialItemState = {
	isFinishedLoading: true,
	errors: null,
	item: null,
	updatedSuccessfully: false,
	updating: false,
};

export const initialPaginationState = {
	data: [],
	total: 0,
	isLoading: false,
};

export const patch = <T>(ctx: StateContext<T>, partToUpdate, patch: ObjectLiteral) => {
	const key = Object.keys(partToUpdate)[0];

	const currentState = ctx.getState()[key];

	const updatedState = { [key]: { ...currentState, ...patch } };

	ctx.patchState(updatedState as Partial<unknown>);
};

export const loadingStart = <T>(ctx: StateContext<T>, partToUpdate) =>
	patch<T>(ctx, partToUpdate, {
		isFinishedLoading: false,
		errors: null,
		updatedSuccessfully: false,
	});
export const loadingStop = <T>(ctx: StateContext<T>, partToUpdate) =>
	patch<T>(ctx, partToUpdate, {
		isFinishedLoading: true,
	});

export const updatingStart = <T>(ctx: StateContext<T>, partToUpdate) =>
	patch<T>(ctx, partToUpdate, {
		updating: true,
		errors: null,
		updatedSuccessfully: false,
	});
export const updatingStop = <T>(ctx: StateContext<T>, partToUpdate) =>
	patch<T>(ctx, partToUpdate, {
		updating: false,
	});

export const setItems = <T, P>(ctx: StateContext<T>, stateProp, items: P[]) => {
	patch<T>(ctx, stateProp, { items });
};
export const setPaginatedItems = <T, P>(
	ctx: StateContext<T>,
	stateProp,
	items: P[],
	total: number,
) => {
	patch<T>(ctx, stateProp, { items, total });
};

export const setItem = <T, P>(ctx: StateContext<T>, stateProp, item: P) => {
	patch<T>(ctx, stateProp, { item });
};

export const handleErrors = <T>(ctx: StateContext<T>, stateProp, errors: string) => {
	patch<T>(ctx, stateProp, { errors });
};

export const updatedSuccessfully = <T>(ctx: StateContext<T>, stateProp) => {
	patch<T>(ctx, stateProp, { updatedSuccessfully: true });
};

export const resetItem = <T>(ctx: StateContext<T>, stateProp) => {
	patch<T>(ctx, stateProp, { ...initialItemState });
};

export function parseHtmlToText(html: string) {
	const parseHtml = parse(html);
	return parseHtml.text;
}
